/**
 * Smith Protective — Sequences page (Iter198 · i18n Iter317).
 *
 * List existing sequences + create/edit + inspect enrollments. Enrolling
 * a prospect is done from the Outreach AI compose panel (deep-link with
 * a "Enroll in sequence" button per contact). This page is the config
 * hub — think Apollo/Salesloft "sequences" tab.
 *
 * Iter317 — Every user-facing string routes through `useLang().t()` so the
 * page follows the platform-wide EN/PT/ES switcher. The status chip's
 * label map now stores i18n keys instead of hardcoded PT copy.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Loader2, RefreshCw, X, Trash2, Plus, Zap, ListChecks,
  ArrowRight, CheckCircle2, StopCircle,
} from "lucide-react";
import Sidebar from "../../components/Sidebar";
import AppHeader from "../../components/SmithAppHeader";
import HelpButton from "../../components/tutorials/HelpButton";
import OutreachTabBar from "./OutreachTabBar";
import { useLang } from "../../i18n/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#0B1120";

const friendlyError = (e, fb = "Failed") =>
  e?.response?.data?.detail || e?.response?.data?.error?.message || e?.message || fb;

export default function SmithSequences() {
  const { t } = useLang();
  const [seqs, setSeqs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [runningNow, setRunningNow] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: sq }, { data: tp }, { data: en }] = await Promise.all([
        axios.get(`${API}/smith/sequences`),
        axios.get(`${API}/smith/outreach/templates`),
        axios.get(`${API}/smith/sequences/enrollments?limit=100`),
      ]);
      setSeqs(sq.items || []);
      setTemplates(tp.items || []);
      setEnrollments(en.items || []);
    } catch (e) {
      toast.error(friendlyError(e, t("seq.toast.load_failed")));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    if (templates.length === 0) {
      toast.error(t("seq.toast.no_templates"));
      return;
    }
    setEditing({
      name: "",
      description: "",
      steps: [
        { delay_days: 0,  template_id: templates[0].id, label: t("seq.step.initial") },
        { delay_days: 3,  template_id: templates[0].id, label: t("seq.step.follow", { n: 1 }) },
        { delay_days: 7,  template_id: templates[0].id, label: t("seq.step.follow", { n: 2 }) },
        { delay_days: 14, template_id: templates[0].id, label: t("seq.step.last") },
      ],
      stop_on_reply: true,
      stop_on_pipeline: ["won", "lost"],
    });
    setEditorOpen(true);
  };

  const openEdit = (s) => { setEditing({ ...s }); setEditorOpen(true); };

  const saveSeq = async () => {
    if (!editing.name || editing.name.length < 2) {
      toast.error(t("seq.toast.name_min")); return;
    }
    setBusy(true);
    try {
      const body = {
        name: editing.name, description: editing.description,
        steps: editing.steps.map((s) => ({
          delay_days: Number(s.delay_days),
          template_id: s.template_id,
          label: s.label,
        })),
        stop_on_reply: editing.stop_on_reply,
        stop_on_pipeline: editing.stop_on_pipeline,
      };
      if (editing.id) {
        await axios.put(`${API}/smith/sequences/${editing.id}`, body);
        toast.success(t("seq.toast.updated"));
      } else {
        await axios.post(`${API}/smith/sequences`, body);
        toast.success(t("seq.toast.created"));
      }
      setEditorOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(friendlyError(e, t("seq.toast.save_failed")));
    } finally { setBusy(false); }
  };

  const deleteSeq = async (s) => {
    if (!confirm(t("seq.confirm.delete", { name: s.name }))) return;
    try {
      await axios.delete(`${API}/smith/sequences/${s.id}`);
      toast.success(t("seq.toast.deleted"));
      load();
    } catch (e) { toast.error(friendlyError(e, t("seq.toast.delete_failed"))); }
  };

  const stopEnrollment = async (e) => {
    if (!confirm(t("seq.confirm.stop", { name: e.company_name }))) return;
    try {
      await axios.post(`${API}/smith/sequences/enrollments/${e.id}/stop`);
      toast.success(t("seq.toast.stopped")); load();
    } catch (err) { toast.error(friendlyError(err, t("seq.toast.delete_failed"))); }
  };

  const runNow = async () => {
    if (!confirm(t("seq.confirm.run_now"))) return;
    setRunningNow(true);
    try {
      const { data } = await axios.post(`${API}/smith/sequences/run-now`);
      toast.success(t("seq.toast.sweep_ok", {
        sent: data.sent, stopped: data.stopped, picked: data.picked,
      }));
      load();
    } catch (e) { toast.error(friendlyError(e, t("seq.toast.sweep_failed"))); }
    finally { setRunningNow(false); }
  };

  const activeCount = enrollments.filter((e) => e.status === "active").length;

  return (
    <div className="min-h-screen text-white" style={{ background: NAVY }}
         data-testid="smith-sequences-page">
      <Sidebar />
      <div className="lg:ml-[var(--al-sidebar-w,260px)]">
        <AppHeader
          testid="smith-sequences-header"
          title={t("seq.header.title")}
          subtitle={t("seq.header.subtitle", { n: seqs.length, active: activeCount })}
          logo={
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                 style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)", color: "white" }}>
              <ListChecks className="w-5 h-5" />
            </div>
          }
        >
          <HelpButton tutorialId="sequences" />
          <button type="button" onClick={load} disabled={loading}
            data-testid="sequences-refresh-btn"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12.5px] font-bold hover:border-white/30 inline-flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> {t("seq.btn.refresh")}
          </button>
          <button type="button" onClick={runNow} disabled={runningNow}
            data-testid="sequences-run-now-btn"
            title={t("seq.btn.run_now_title")}
            className="h-9 px-3 rounded-lg text-[12.5px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)" }}>
            {runningNow ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />} {t("seq.btn.run_now")}
          </button>
          <button type="button" onClick={openNew}
            data-testid="sequences-new-btn"
            className="h-9 px-3 rounded-lg text-[12.5px] font-bold text-white inline-flex items-center gap-1.5"
            style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
            <Plus className="w-3.5 h-3.5" /> {t("seq.btn.new")}
          </button>
        </AppHeader>

        <div className="max-w-[1500px] mx-auto px-3 sm:px-6 lg:px-8 pt-3">
          <OutreachTabBar current="sequences" />
        </div>

        <main className="px-3 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-[1500px] mx-auto pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <section data-testid="sequences-list">
              <h2 className="text-[11px] font-extrabold uppercase tracking-widest text-slate-400 mb-2">{t("seq.list.title")}</h2>
              {loading ? (
                <div className="py-8 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#DC2626]" /></div>
              ) : seqs.length === 0 ? (
                <div className="rounded-2xl border border-white/10 p-6 text-center text-[12px] text-slate-500"
                     style={{ background: "rgba(15,23,42,0.7)" }}>
                  {t("seq.list.empty")}
                </div>
              ) : (
                <div className="space-y-2">
                  {seqs.map((s, i) => (
                    <div key={s.id} data-testid={`sequences-card-${i}`}
                         className="rounded-xl border border-white/10 p-3 space-y-2"
                         style={{ background: "rgba(15,23,42,0.7)" }}>
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-extrabold text-white flex-1 truncate">{s.name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold">
                          {t("seq.card.active_pill", { n: s.active_enrollments })}
                        </span>
                        <button type="button" onClick={() => openEdit(s)}
                                data-testid={`sequences-edit-${i}`}
                                className="text-[10.5px] font-bold text-slate-300 hover:text-white">{t("seq.card.edit")}</button>
                        <button type="button" onClick={() => deleteSeq(s)}
                                data-testid={`sequences-delete-${i}`}
                                className="w-6 h-6 rounded hover:bg-red-500/10 text-slate-500 hover:text-red-400 flex items-center justify-center">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      {s.description && <div className="text-[10.5px] text-slate-400">{s.description}</div>}
                      <div className="flex items-center gap-1 text-[10.5px] flex-wrap">
                        {s.steps.map((st, j) => (
                          <span key={j} className="inline-flex items-center gap-1">
                            <span className="px-1.5 py-0.5 rounded bg-slate-500/20 font-bold text-slate-300">
                              {t("seq.card.day", { n: st.delay_days })}
                            </span>
                            {j < s.steps.length - 1 && <ArrowRight className="w-2.5 h-2.5 text-slate-500" />}
                          </span>
                        ))}
                      </div>
                      <div className="text-[10px] text-slate-500 flex gap-3">
                        <span>{t("seq.card.stats_active")}: <b className="text-emerald-400 tabular-nums">{s.active_enrollments}</b></span>
                        <span>{t("seq.card.stats_completed")}: <b className="text-blue-400 tabular-nums">{s.completed_enrollments}</b></span>
                        <span>{t("seq.card.stats_stopped")}: <b className="text-red-400 tabular-nums">{s.stopped_enrollments}</b></span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section data-testid="sequences-enrollments-list">
              <h2 className="text-[11px] font-extrabold uppercase tracking-widest text-slate-400 mb-2">
                {t("seq.enroll.title", { n: enrollments.length })}
              </h2>
              {enrollments.length === 0 ? (
                <div className="rounded-2xl border border-white/10 p-6 text-center text-[12px] text-slate-500"
                     style={{ background: "rgba(15,23,42,0.7)" }}>
                  {t("seq.enroll.empty_prefix")}{" "}
                  <Link className="text-[#DC2626] font-bold" to="/smith/outreach">{t("seq.enroll.empty_link")}</Link>
                  {t("seq.enroll.empty_suffix")}
                </div>
              ) : (
                <div className="rounded-2xl border border-white/10 overflow-hidden"
                     style={{ background: "rgba(15,23,42,0.7)" }}>
                  <div className="max-h-[75vh] overflow-y-auto divide-y divide-white/5">
                    {enrollments.map((e, i) => (
                      <div key={e.id} data-testid={`sequences-enrollment-row-${i}`}
                           className="px-3 py-2 flex items-center gap-2">
                        <StatusChip status={e.status} t={t} />
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] font-bold text-white truncate">{e.company_name}</div>
                          <div className="text-[10.5px] text-slate-400 truncate">
                            {e.sequence_name} · {t("seq.enroll.step", { n: e.current_step })} · {e.to_email}
                          </div>
                        </div>
                        {e.status === "active" && (
                          <>
                            <span className="text-[10px] text-slate-500 tabular-nums shrink-0">
                              {t("seq.enroll.next", { at: e.next_run_at ? new Date(e.next_run_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—" })}
                            </span>
                            <button type="button" onClick={() => stopEnrollment(e)}
                                    data-testid={`sequences-stop-enrollment-${i}`}
                                    className="w-6 h-6 rounded hover:bg-red-500/10 text-slate-500 hover:text-red-400 flex items-center justify-center">
                              <StopCircle className="w-3 h-3" />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          </div>
        </main>
      </div>

      {editorOpen && editing && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
             onClick={() => setEditorOpen(false)}
             data-testid="sequences-editor">
          <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl border"
               style={{ background: "#0B1120", borderColor: "rgba(220,38,38,0.35)" }}
               onClick={(ev) => ev.stopPropagation()}>
            <header className="px-5 py-4 border-b border-white/10 flex items-center gap-3">
              <ListChecks className="w-5 h-5 text-[#DC2626]" />
              <div className="text-[14px] font-extrabold text-white flex-1">
                {editing.id ? t("seq.editor.title_edit") : t("seq.editor.title_new")}
              </div>
              <button onClick={() => setEditorOpen(false)}
                      data-testid="sequences-editor-close"
                      className="w-8 h-8 rounded hover:bg-white/5 text-slate-400 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </header>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("seq.editor.name")}</label>
                <input value={editing.name}
                       data-testid="sequences-editor-name"
                       onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                       className="mt-1 w-full h-9 px-3 rounded-lg bg-black/40 border border-white/15 text-[12.5px] text-white outline-none focus:border-[#DC2626]" />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-widest text-slate-400">{t("seq.editor.description")}</label>
                <input value={editing.description || ""}
                       data-testid="sequences-editor-description"
                       onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                       className="mt-1 w-full h-9 px-3 rounded-lg bg-black/40 border border-white/15 text-[12.5px] text-white outline-none focus:border-[#DC2626]" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-[11px] font-bold uppercase tracking-widest text-slate-400 flex-1">{t("seq.editor.steps")}</label>
                  <button type="button" onClick={() => setEditing({ ...editing, steps: [...editing.steps, { delay_days: 7, template_id: templates[0]?.id, label: `Step ${editing.steps.length + 1}` }] })}
                          data-testid="sequences-editor-add-step"
                          disabled={editing.steps.length >= 12}
                          className="h-7 px-2 rounded text-[10.5px] font-bold text-slate-300 border border-white/15 hover:border-white/30 inline-flex items-center gap-1 disabled:opacity-30">
                    <Plus className="w-3 h-3" /> {t("seq.editor.add_step")}
                  </button>
                </div>
                <div className="space-y-2">
                  {editing.steps.map((step, idx) => (
                    <div key={idx} className="flex items-center gap-2"
                         data-testid={`sequences-editor-step-${idx}`}>
                      <span className="text-[10px] text-slate-500 w-8">#{idx + 1}</span>
                      <input type="number" min={0} max={90}
                             value={step.delay_days}
                             onChange={(e) => {
                               const s = [...editing.steps];
                               s[idx] = { ...s[idx], delay_days: Number(e.target.value) };
                               setEditing({ ...editing, steps: s });
                             }}
                             data-testid={`sequences-editor-step-${idx}-delay`}
                             className="w-16 h-8 px-2 rounded bg-black/40 border border-white/15 text-[12px] text-white outline-none" />
                      <span className="text-[10px] text-slate-500">{t("seq.editor.days")}</span>
                      <select value={step.template_id}
                              onChange={(e) => {
                                const s = [...editing.steps];
                                s[idx] = { ...s[idx], template_id: e.target.value };
                                setEditing({ ...editing, steps: s });
                              }}
                              data-testid={`sequences-editor-step-${idx}-template`}
                              className="flex-1 h-8 px-2 rounded bg-black/40 border border-white/15 text-[12px] text-white outline-none">
                        {templates.map((tp) => (
                          <option key={tp.id} value={tp.id}>{tp.name}</option>
                        ))}
                      </select>
                      <button type="button" onClick={() => {
                                const s = editing.steps.filter((_, i) => i !== idx);
                                setEditing({ ...editing, steps: s });
                              }}
                              disabled={editing.steps.length <= 1}
                              data-testid={`sequences-editor-step-${idx}-delete`}
                              className="w-7 h-7 rounded hover:bg-red-500/10 text-slate-500 hover:text-red-400 flex items-center justify-center disabled:opacity-30">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-[11.5px] text-slate-300"
                     data-testid="sequences-editor-stop-on-reply">
                <input type="checkbox" checked={editing.stop_on_reply}
                       onChange={(e) => setEditing({ ...editing, stop_on_reply: e.target.checked })} />
                {t("seq.editor.stop_on_reply")}
              </label>
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
                <button type="button" onClick={() => setEditorOpen(false)}
                        data-testid="sequences-editor-cancel"
                        className="h-9 px-4 rounded-lg text-[12px] font-bold text-slate-300 hover:text-white">
                  {t("seq.editor.cancel")}
                </button>
                <button type="button" onClick={saveSeq} disabled={busy}
                        data-testid="sequences-editor-save"
                        className="h-9 px-4 rounded-lg text-[12px] font-extrabold text-white inline-flex items-center gap-2 disabled:opacity-50"
                        style={{ background: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)" }}>
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  {t("seq.editor.save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusChip({ status, t }) {
  const map = {
    active:           { i18n: "seq.status.active",           bg: "bg-emerald-500/20", fg: "text-emerald-300" },
    completed:        { i18n: "seq.status.completed",        bg: "bg-blue-500/20",    fg: "text-blue-300" },
    stopped_reply:    { i18n: "seq.status.stopped_reply",    bg: "bg-amber-500/20",   fg: "text-amber-300" },
    stopped_manual:   { i18n: "seq.status.stopped_manual",   bg: "bg-slate-500/20",   fg: "text-slate-300" },
    stopped_pipeline: { i18n: "seq.status.stopped_pipeline", bg: "bg-purple-500/20",  fg: "text-purple-300" },
  };
  const m = map[status] || map.active;
  return (
    <span className={`text-[9.5px] font-bold px-1.5 py-0.5 rounded ${m.bg} ${m.fg} shrink-0`}>
      {t(m.i18n)}
    </span>
  );
}
